import React from "react";
import { useParams } from "react-router-dom";
import { Dealer } from "@/types/dealer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Phone, Star, Clock, Info } from "lucide-react";
import { DealerSEO } from "./DealerSEO";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { HydratedDealerRatings } from "./HydratedDealerRatings";
import { Rating } from "@/hooks/useDealerRatings";
import { DealerInfoCard } from "./DealerInfoCard";
import { formatCurrency } from "@/lib/utils";
import { DealerLocationMap } from "@/components/DealerLocationMap";
import { DealerSocialLinks } from "./DealerSocialLinks";

interface ServerDealerContentProps {
  dealer?: Dealer | null;
  citySlug?: string;
  userDistance?: string | null;
}

/**
 * Server-side safe version of DealerContent
 * This component renders a simplified version of the dealer detail during server-side rendering
 */
export function ServerDealerContent({ dealer: propDealer, citySlug, userDistance }: ServerDealerContentProps) {
  const { formatCityName, createDealerCanonicalPath } = useDealerUtils();
  const { id } = useParams<{ id: string }>();
  
  // Try to get dealer data from props or from global context
  const globalStore = typeof globalThis !== 'undefined' ? (globalThis as any).__SERVER_STORE : null;
  const serverDealer = globalStore?.dealerData?.[id];
  
  // Get metal prices from server store
  const goldPrice = globalStore?.rawGoldPrice || 0;
  const silverPrice = globalStore?.rawSilverPrice || 0;
  
  // Use dealer from props or from server store
  const dealer = propDealer || serverDealer;
  
  // If still no dealer, render a loading state
  if (!dealer) {
    console.log("[ServerDealerContent] No dealer data found, rendering loading state");
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Caricamento dettagli compro oro...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  console.log("[ServerDealerContent] Rendering dealer:", dealer.name);
  
  // Handle missing rating with a default value
  const rating = typeof dealer.rating === 'number' ? dealer.rating : 0;
  
  // Format city from dealer or from citySlug
  const city = dealer.city || (citySlug ? formatCityName(citySlug) : '');
  
  return (
    <>
      {/* Add SEO metadata for server-side rendering */}
      <DealerSEO 
        dealer={dealer} 
        formattedCity={city} 
        canonicalPath={createDealerCanonicalPath(dealer.id, dealer.city || citySlug || '')}
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Use DealerInfoCard to display dealer information with gold price */}
          <DealerInfoCard 
            dealer={dealer}
            goldPrice={goldPrice}
            silverPrice={silverPrice}
            isAuthenticated={false}
            handleActivateDealer={undefined}
            userDistance={userDistance}
          />
          
          {/* Add hydrated dealer ratings */}
          <HydratedDealerRatings 
            dealerId={dealer.id}
            dealerName={dealer.name}
          />
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Posizione</CardTitle>
            </CardHeader>
            <CardContent>
              <DealerLocationMap 
                latitude={dealer.latitude} 
                longitude={dealer.longitude} 
                name={dealer.name}
                address={dealer.address || 'Indirizzo non disponibile'}
                mapId={`dealer-map-${dealer.id}`}
              />
              <p className="text-xs text-muted-foreground mt-2 text-center">
                {dealer.address} {dealer.postal_code && `- ${dealer.postal_code}`}
              </p>
              {userDistance && (
                <p className="text-xs text-muted-foreground mt-1 text-center font-medium">
                  📍 Distanza: {userDistance}
                </p>
              )}
            </CardContent>
          </Card>

          <DealerSocialLinks
            facebook={dealer.facebook}
            instagram={dealer.instagram}
            twitter={dealer.twitter}
            youtube={dealer.youtube}
            linkedin={dealer.linkedin}
          />
        </div>
      </div>
    </>
  );
}
